import React, { useCallback, useEffect, useState } from "react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import api, { apiErr } from "@/lib/api";
import { eur, num, RANGES } from "@/lib/format";
import { PageHeader, Card, Th, Td, Empty } from "./ui";
import { Download, Info, Loader2 } from "lucide-react";
import { toast } from "sonner";

const COLORS = ["#FF8C00", "#FFD700", "#00CED1", "#B5179E", "#B19CD9"];
const INSIGHT_RANGES = [...RANGES.filter((r) => r.key !== "custom"),
  { key: "this_quarter", label: "Dit kwartaal" }, { key: "last_year", label: "Vorig jaar" }, { key: "custom", label: "Aangepast" }];

export default function Insights() {
  const [range, setRange] = useState("this_year");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [series, setSeries] = useState([]);
  const [top, setTop] = useState([]);
  const [vat, setVat] = useState({ rows: [], method: "" });
  const [customers, setCustomers] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [metric, setMetric] = useState("inc");

  const load = useCallback(async () => {
    setLoading(true);
    const p = { range };
    if (range === "custom" && custom.start && custom.end) { p.start = custom.start; p.end = custom.end; }
    try {
      const [rv, tp, vt, cu, fn] = await Promise.all([
        api.get("/admin/dashboard/revenue", { params: p }),
        api.get("/admin/dashboard/top-products", { params: { ...p, metric: "inc" } }),
        api.get("/admin/insights/vat", { params: p }),
        api.get("/admin/insights/customers", { params: p }),
        api.get("/admin/insights/funnel", { params: p }),
      ]);
      setSeries(rv.data); setTop(tp.data); setVat(vt.data); setCustomers(cu.data); setFunnel(fn.data);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  }, [range, custom]);

  useEffect(() => { if (range !== "custom" || (custom.start && custom.end)) load(); }, [load, range, custom]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/orders-export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "soulfoodcity-bestellingen.csv"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const exportPdf = async () => {
    try {
      const p = { range };
      if (range === "custom" && custom.start && custom.end) { p.start = custom.start; p.end = custom.end; }
      const res = await api.get("/admin/insights/revenue-report.pdf", { params: p, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "soulfoodcity-omzetrapport.pdf"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const totalRev = series.reduce((s, x) => s + x.inc, 0);
  const totalEx = series.reduce((s, x) => s + x.ex, 0);
  const totalVat = series.reduce((s, x) => s + x.vat, 0);
  const totalOrders = series.reduce((s, x) => s + x.orders, 0);

  return (
    <div>
      <PageHeader title="Omzet, btw & inzichten" subtitle="Server-side berekend · Europe/Amsterdam · EUR">
        <div className="flex flex-wrap items-center gap-2">
          <select data-testid="insights-range" value={range} onChange={(e) => setRange(e.target.value)} className="rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-sm text-white focus:outline-none">
            {INSIGHT_RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
          <button data-testid="insights-export" onClick={exportCsv} className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-white/80 hover:bg-white/10"><Download size={15} /> Export CSV</button>
          <button data-testid="insights-pdf" onClick={exportPdf} className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-white/80 hover:bg-white/10"><Download size={15} /> PDF-rapport</button>
          {loading && <Loader2 className="animate-spin text-sfc-orange" size={18} />}
        </div>
      </PageHeader>

      {range === "custom" && (
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <input type="date" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })} className="rounded-xl border border-white/10 bg-sfc-night px-3 py-2 text-sm text-white" />
          <span className="text-white/40">tot</span>
          <input type="date" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })} className="rounded-xl border border-white/10 bg-sfc-night px-3 py-2 text-sm text-white" />
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card className="p-5"><p className="text-xs text-white/50">Omzet incl. btw</p><p className="mt-2 font-display text-2xl text-white">{eur(totalRev)}</p></Card>
        <Card className="p-5"><p className="text-xs text-white/50">Omzet excl. btw</p><p className="mt-2 font-display text-2xl text-white">{eur(totalEx)}</p></Card>
        <Card className="p-5"><p className="text-xs text-white/50">Te dragen btw</p><p className="mt-2 font-display text-2xl text-white">{eur(totalVat)}</p></Card>
        <Card className="p-5"><p className="text-xs text-white/50">Betaalde orders</p><p className="mt-2 font-display text-2xl text-white">{num(totalOrders)}</p></Card>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <Card className="p-6 lg:col-span-2">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg text-white">Orderfunnel</h3>
            {funnel && <span className="text-sm text-sfc-cyan">Conversie: {funnel.conversion_pct}%</span>}
          </div>
          {funnel ? (
            <div className="mt-5 space-y-4" data-testid="insights-funnel">
              {funnel.stages.map((s) => (
                <div key={s.key}>
                  <div className="flex items-center justify-between text-sm text-white/70">
                    <span>{s.label}</span>
                    <span><span className="font-semibold text-white">{s.count}</span> · {s.pct}%</span>
                  </div>
                  <div className="mt-1.5 h-3 w-full overflow-hidden rounded-full bg-white/[0.06]">
                    <div className="h-full rounded-full bg-gradient-to-r from-sfc-orange to-sfc-red" style={{ width: `${Math.max(s.pct, 2)}%` }} />
                  </div>
                </div>
              ))}
              <div className="flex flex-wrap gap-6 border-t border-white/10 pt-4 text-sm">
                <span className="text-white/60">In handmatige controle: <span className="font-semibold text-white">{funnel.in_review}</span></span>
                <span className="text-white/60">Betaalde omzet: <span className="font-semibold text-white">{eur(funnel.paid_revenue_inc)}</span></span>
              </div>
            </div>
          ) : <Empty text="Geen funnel-gegevens." />}
        </Card>
        <Card className="p-6">
          <h3 className="font-display text-lg text-white">Omzet per smaak</h3>
          {top.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart layout="vertical" data={top} margin={{ left: 10 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={110} tick={{ fill: "#ffffff70", fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#0e0722", border: "1px solid #ffffff20", borderRadius: 12 }} formatter={(v) => eur(v)} />
                <Bar dataKey="inc" radius={[0, 6, 6, 0]}>{top.map((s, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty text="Nog geen verkopen." />}
        </Card>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-1">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg text-white">Omzetontwikkeling</h3>
            <div className="flex rounded-full border border-white/10 p-1 text-xs">
              {[["inc", "Incl."], ["ex", "Excl."], ["vat", "Btw"]].map(([key, lbl]) => (
                <button key={key} onClick={() => setMetric(key)} className={`rounded-full px-3 py-1 ${metric === key ? "bg-sfc-orange text-white" : "text-white/60"}`}>{lbl}</button>
              ))}
            </div>
          </div>
          {series.some((s) => s.inc || s.ex || s.vat) ? (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={series} margin={{ top: 20, right: 10, left: -10 }}>
                <defs><linearGradient id="ins" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#00CED1" stopOpacity={0.5} /><stop offset="100%" stopColor="#00CED1" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                <XAxis dataKey="date" tick={{ fill: "#ffffff60", fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fill: "#ffffff60", fontSize: 11 }} tickFormatter={(v) => `€${v}`} />
                <Tooltip contentStyle={{ background: "#0e0722", border: "1px solid #ffffff20", borderRadius: 12 }} formatter={(v) => eur(v)} />
                <Area type="monotone" dataKey={metric} stroke="#00CED1" strokeWidth={2} fill="url(#ins)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <Empty text="Geen betaalde bestellingen in deze periode." />}
        </Card>
      </div>

      <Card className="mt-6 p-6">
        <div className="flex items-center gap-2"><h3 className="font-display text-lg text-white">Btw-overzicht per tarief & maand</h3></div>
        <p className="mt-1 flex items-start gap-2 text-xs text-white/40"><Info size={13} className="mt-0.5" /> {vat.method}</p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full"><thead className="border-b border-white/10"><tr><Th>Maand</Th><Th>Btw-tarief</Th><Th>Grondslag (excl. btw)</Th><Th>Btw</Th></tr></thead>
            <tbody>
              {vat.rows?.map((r, i) => (
                <tr key={i} className="border-b border-white/5"><Td>{r.month}</Td><Td>{r.rate}%</Td><Td>{eur(r.base)}</Td><Td className="text-sfc-yellow">{eur(r.vat)}</Td></tr>
              ))}
            </tbody>
          </table>
          {(!vat.rows || vat.rows.length === 0) && <Empty text="Geen btw-gegevens in deze periode." />}
        </div>
      </Card>

      <Card className="mt-6 p-6">
        <h3 className="font-display text-lg text-white">Topklanten</h3>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full"><thead className="border-b border-white/10"><tr><Th>Bedrijf</Th><Th>KvK</Th><Th>Orders</Th><Th>Omzet</Th><Th>Gem. orderwaarde</Th></tr></thead>
            <tbody>
              {customers.map((c, i) => (
                <tr key={i} className="border-b border-white/5"><Td>{c.company_name}</Td><Td>{c.kvk_number}</Td><Td>{c.orders}</Td><Td>{eur(c.revenue)}</Td><Td>{eur(c.aov)}</Td></tr>
              ))}
            </tbody>
          </table>
          {customers.length === 0 && <Empty text="Nog geen betaalde klanten." />}
        </div>
      </Card>
    </div>
  );
}
